import {
  SB,
  createMessage,
  createSharedStateMessage,
  createSharedStateMessages,
} from '@play-co/replicant';
import { teamSharedStateSchema } from './team.schema';
import {
  HELP_REQUEST_EXPIRATION,
  MAX_CHAT_MESSAGES,
  MAX_MEMBERS,
  TEAM_EVENT_EXPIRATION,
  TeamRole,
  energyRequestAmount,
} from './team.ruleset';
import createMessages from '../config/messages/createMessages';
import {
  getCurrentTeamEventId,
  getPreviousTeamEventId,
  getTeamEventEndDate,
} from './team.getters';
import { canParticipateOnTeamCompetition } from './competition/teamCompetition.getters';
import * as Inventory from '../inventory';
import { calculateTeamChestTargetScore } from './chest/teamChest.getters';
import { getDummyAPI } from '../inventory/inventory.utils';
import { duration } from '../utils/duration';

export const teamMessages = createSharedStateMessages(teamSharedStateSchema)({
  joinTeam: createSharedStateMessage(
    SB.object({
      userId: SB.string().optional(),
      timestamp: SB.number().optional(),
    }),
    (state, { userId, timestamp }, meta) => {
      userId = userId ?? meta.senderId;
      const membersIds = Object.keys(state.global.members);
      if (membersIds.includes(userId)) return;
      if (membersIds.length >= MAX_MEMBERS) return;

      const isCreator = !membersIds.length;

      state.global.members[userId] = {
        joinedAt: timestamp ?? meta.timestamp,
        role: isCreator ? TeamRole.Admin : TeamRole.Member,
        contributions: {},
        helps: [],
      };

      // Set the team createdAt timestamp.
      if (isCreator) {
        state.global.createdTimestamp = timestamp ?? meta.timestamp;
      }

      // Update the team modifiedAt timestamp.
      state.global.modifiedAt = timestamp ?? meta.timestamp;
    },
  ),

  removeMember: createSharedStateMessage(
    SB.object({
      userId: SB.string().optional(),
      timestamp: SB.number().optional(),
    }),
    (state, { userId, timestamp }, meta) => {
      userId = userId ?? meta.senderId;

      // User is not in the team.
      if (!Object.keys(state.global.members).includes(userId)) return;

      // Only admins can remove other members.
      if (
        userId !== meta.senderId &&
        state.global.members[meta.senderId].role !== TeamRole.Admin
      ) {
        return;
      }

      // If user is the only admin, promote a random member to admin.
      if (state.global.members[userId].role === TeamRole.Admin) {
        const admins = Object.values(state.global.members).filter(
          (member) => member.role === TeamRole.Admin,
        );
        const membersIds = Object.keys(state.global.members);
        if (admins.length === 1) {
          const newAdmin = (membersIds.indexOf(userId) + 1) % membersIds.length;
          state.global.members[membersIds[newAdmin]].role = TeamRole.Admin;
        }
      }

      // Remove user from team.
      delete state.global.members[userId];

      // Update the team modifiedAt timestamp.
      state.global.modifiedAt = timestamp ?? meta.timestamp;

      // Remove chat messages from the removed user.
      state.global.chat = state.global.chat.filter(
        (message) => message.senderId !== userId,
      );
    },
  ),

  updateTeamProfile: createSharedStateMessage(
    SB.object({
      name: SB.string().optional(),
      description: SB.string().optional(),
      icon: SB.string().optional(),
      levelRequired: SB.int().optional(),
      timestamp: SB.number().optional(),
    }),
    (state, args, meta) => {
      // Only admins can update the team profile.
      if (!process.env.IS_DEVELOPMENT) {
        if (state.global.members[meta.senderId].role !== TeamRole.Admin) return;
      }

      // Update the team modifiedAt timestamp.
      state.global.modifiedAt = args.timestamp ?? meta.timestamp;

      // Old messages don't have timestamps, only team profile data.
      // To avoid creating a whole new message, we just delete the timestamp.
      delete args.timestamp;

      // Update the team profile.
      Object.assign(state.global.profile, args);
    },
  ),

  /**
   * @deprecated Use sendChatMessageToTeam instead.
   */
  sendChatMessage: createSharedStateMessage(
    SB.object({
      type: SB.string(),
      id: SB.string().optional(),
      value: SB.int().optional(),
    }),
    (state, contents, meta) => {
      // Only one request per type per member.
      if (
        contents.type === 'energyRequest' ||
        contents.type === 'helpRequest'
      ) {
        state.global.chat = state.global.chat.filter(
          (message) =>
            message.senderId !== meta.senderId ||
            message.contents.type !== contents.type,
        );
      }

      // Append the chat message.
      state.global.chat.push({
        sentAt: meta.timestamp,
        senderId: meta.senderId,
        contents,
      });

      // Clean chat history if it's too long.
      const chatLength = state.global.chat.length;
      if (chatLength >= MAX_CHAT_MESSAGES) {
        state.global.chat.splice(0, chatLength - MAX_CHAT_MESSAGES);
      }

      // Update the team modifiedAt timestamp.
      state.global.modifiedAt = meta.timestamp;
    },
  ),

  sendChatMessageToTeam: createSharedStateMessage(
    SB.object({
      timestamp: SB.number(),
      contents: SB.object({
        type: SB.string(),
        id: SB.string().optional(),
        value: SB.int().optional(),
      }),
    }),
    (state, { timestamp, contents }, meta) => {
      // Only one request per type per member.
      if (
        contents.type === 'energyRequest' ||
        contents.type === 'helpRequest'
      ) {
        state.global.chat = state.global.chat.filter(
          (message) =>
            message.senderId !== meta.senderId ||
            message.contents.type !== contents.type,
        );
      }

      // Append the chat message.
      state.global.chat.push({
        sentAt: timestamp,
        senderId: meta.senderId,
        contents,
      });

      // Clean chat history if it's too long.
      const chatLength = state.global.chat.length;
      if (chatLength >= MAX_CHAT_MESSAGES) {
        state.global.chat.splice(0, chatLength - MAX_CHAT_MESSAGES);
      }

      // Update the team modifiedAt timestamp.
      state.global.modifiedAt = timestamp;
    },
  ),

  createEvent: createSharedStateMessage(
    SB.object({
      timestamp: SB.number().optional(),
      eventId: SB.string(),
      sharedStateId: SB.string(),
      eventType: SB.string().optional(),
    }),

    (state, { eventType, timestamp, eventId, sharedStateId }, meta) => {
      // Only members can create events.
      if (!process.env.IS_DEVELOPMENT) {
        if (!state.global.members[meta.senderId]) return;
      }

      // Event already exists. Bail.
      const isNewEvent = !state.global.events[eventId];
      if (!isNewEvent) return;

      // Calculate initial team score for the event, based on current members contributions.
      const score = Object.values(state.global.members).reduce(
        (score, member) => {
          const memberScore = member.contributions[eventId]?.score ?? 0;
          return score + memberScore;
        },
        0,
      );

      const previousEventId = getPreviousTeamEventId(
        state,
        timestamp ?? meta.timestamp,
      );

      // Create the event.
      state.global.events[eventId] = {
        type: eventType ?? 'competition',
        createdAt: timestamp ?? meta.timestamp,
        modifiedAt: timestamp ?? meta.timestamp,
        score,
        sharedStateId,
        targetScore:
          eventType === 'chest'
            ? calculateTeamChestTargetScore(state, previousEventId)
            : undefined,
      };
    },
  ),

  postEventScore: createSharedStateMessage(
    SB.object({
      score: SB.int(),
      timestamp: SB.int().optional(),
    }),
    (state, { timestamp, score }, meta) => {
      // Only members can post scores.
      const member = state.global.members[meta.senderId];
      if (!member) return;

      const now = timestamp ?? meta.timestamp;
      // Cannot post score if the team cannot participate on team competition.
      // We could check if the team is participating on the current event, but that wouldn't allow for retroactive scores.
      // Ie. If matchmaking didn't run but players are already submitting scores.
      if (!canParticipateOnTeamCompetition(state)) return;

      // Cannot post score if there're no events running.
      const eventId = getCurrentTeamEventId(now);
      if (!eventId) return;

      // Because scores can be retroactive, the event might not exist yet.
      // The event score will be set on its creation, based on existing contributions.
      const event = state.global.events[eventId];
      if (!event) return;

      // Ignore contribution if chest event is reached target score
      if (
        event?.type === 'chest' &&
        event.targetScore &&
        event.score >= event.targetScore
      )
        return;

      // Create the member contribution if it doesn't exist.
      if (!member.contributions[eventId]) {
        member.contributions[eventId] = {
          score: 0,
          createdAt: now,
          modifiedAt: now,
        };
      }

      // Update the member contribution.
      member.contributions[eventId].score += score;
      member.contributions[eventId].modifiedAt = now;

      // Update event score
      event.score += score;
      event.modifiedAt = now;
    },
  ),

  claimRewards: createSharedStateMessage(
    SB.object({ timestamp: SB.number().optional() }),
    (state, { timestamp }, meta) => {
      // Only members can claim rewards.
      const member = state.global.members[meta.senderId];
      if (!member) return;

      Object.keys(member.contributions).forEach((eventId) => {
        // Should not claim rewards if already claimed.
        if (member.contributions[eventId].claimedRewardsAt) return;

        // Claim rewards.
        member.contributions[eventId].claimedRewardsAt =
          timestamp ?? meta.timestamp;
      });
    },
  ),

  /**
   * @deprecated Use claimRewards instead.
   * Kept for backwards compatibility.
   */
  claimTeamChestRewards: createSharedStateMessage(
    SB.object({ timestamp: SB.number().optional() }),
    (state, { timestamp }, meta) => {
      // Only members can claim rewards.
      const member = state.global.members[meta.senderId];
      if (!member) return;

      Object.keys(member.contributions).forEach((eventId) => {
        const event = state.global.events[eventId];

        // Only team chest events should be considered.
        if (event?.type !== 'chest') return;

        // Should not claim rewards if already claimed.
        if (member.contributions[eventId].claimedRewardsAt) return;

        // Claim rewards.
        member.contributions[eventId].claimedRewardsAt =
          timestamp ?? meta.timestamp;
      });
    },
  ),

  sendEnergyHelp: createSharedStateMessage(
    SB.object({ targetId: SB.string(), timestamp: SB.number().optional() }),
    (state, args, meta) => {
      // Only members can send help.
      const member = state.global.members[meta.senderId];
      if (!member) return;

      // Only members can receive help.
      const target = state.global.members[args.targetId];
      if (!target) return;

      // Only one energy request exists per player
      const energyRequest = state.global.chat.find(
        (message) =>
          message.senderId === args.targetId &&
          message.contents.type === 'energyRequest',
      );

      // Help request is gone or completed
      if (
        !energyRequest ||
        energyRequest.contents.value! >= energyRequestAmount
      ) {
        return;
      }

      // To prevent leaving and rejoining the team
      // and sending energy to the same request multiple tiemes
      if (energyRequest.sentAt < member.joinedAt) {
        return;
      }

      const now = args.timestamp ?? meta.timestamp;
      member.helps.push({
        createdAt: now,
        target: args.targetId,
        type: 'energyRequest',
        requestId: energyRequest.sentAt,
      });

      energyRequest.contents.value = (energyRequest.contents.value ?? 0) + 1;

      if (energyRequest.contents.value >= energyRequestAmount) {
        state.global.chat = state.global.chat.filter(
          (message) => message !== energyRequest,
        );
      }

      // Cleanup old helps.
      member.helps = member.helps.filter(
        (help) => now - help.createdAt < HELP_REQUEST_EXPIRATION,
      );
    },
  ),

  sendCareHelp: createSharedStateMessage(
    SB.object({ targetId: SB.string(), timestamp: SB.number().optional() }),
    (state, args, meta) => {
      // Only members can send help.
      const member = state.global.members[meta.senderId];
      if (!member) return;

      // Only members can receive help.
      const target = state.global.members[args.targetId];
      if (!target) return;

      const helpRequest = state.global.chat.find(
        (message) =>
          message.senderId === args.targetId &&
          message.contents.type === 'helpRequest',
      );

      // Help request is gone (completed)
      if (!helpRequest) {
        return;
      }

      member.helps.push({
        createdAt: args.timestamp ?? meta.timestamp,
        target: args.targetId,
        type: 'helpRequest',
        requestId: helpRequest.sentAt,
      });

      // Once the het has been cared for, we don't need to keep the request around.
      state.global.chat = state.global.chat.filter(
        (message) => message !== helpRequest,
      );
    },
  ),

  clearExpiredEvents: createSharedStateMessage(
    SB.object({ now: SB.number() }),
    (state, { now }) => {
      // Figure out which events have expired.
      const expired = Object.keys(state.global.events).filter((eventId) => {
        let eventEndDate = getTeamEventEndDate(eventId);
        // Could not determine the event end date. This could happen if the schedule has changed.
        if (!eventEndDate) {
          // In this case we assume that the event ended 7 days after it was created.
          // We can safely do that because all our events never last more than that.
          eventEndDate =
            state.global.events[eventId].createdAt + duration({ days: 7 });
        }
        const age = now - eventEndDate;
        return age > TEAM_EVENT_EXPIRATION;
      });

      // Clear expired events from the team state.
      expired.forEach((eventId) => {
        delete state.global.events[eventId];
      });

      // Clear expired events from the members contributions.
      Object.values(state.global.members).forEach((member) => {
        expired.forEach((eventId) => {
          delete member.contributions[eventId];
        });

        // Clear old help messages from the members
        member.helps = member.helps.filter(
          (help) => now - help.createdAt < HELP_REQUEST_EXPIRATION,
        );
      });
    },
  ),
});

export const teamMemberMessages = createMessages({
  kickFromTeam: createMessage(SB.object({}), (state, _, meta) => {
    delete state.team.currentTeamId;
  }),

  sendEnergy: createMessage(
    SB.object({ timestamp: SB.number().optional() }),
    (state, { timestamp }, meta) => {
      const now = timestamp ?? meta.timestamp;

      // In case the message arrived late and the timestamp is in the past.
      if (now < state.inventory.energyRechargeStartTime) {
        return;
      }

      // Energy maxed out. Bail.
      if (
        Inventory.getters.getEnergy(state, now) >=
        Inventory.getters.getMaxEnergy(state)
      ) {
        return;
      }

      Inventory.modifiers.addEnergy(
        state,
        1,
        now,
        { feature: 'message' },
        getDummyAPI(),
      );
    },
  ),
});
